import { FC, useEffect, useMemo, useRef } from 'react';
import { parseEmbedSnippet } from './parseEmbedSnippet.ts';
import { createOnLoadCallback } from './createOnLoadCallback.ts';
import { isDntEnabled } from '@utils/dnt.ts';

export interface ThirdPartyEmbedProps {
  snippet?: string;
  async?: boolean;
  notForDnt?: boolean;
}

function getEmbedErrorMessage(
  snippet: string,
  error: string | null
): string | null {
  if (error) {
    return error;
  }
  return snippet.trim()
    ? null
    : 'Third-party embed not configured (configure in layout editor)';
}

function injectScriptToHead(
  src: string,
  loadAsync: boolean,
  callback: () => void
): void {
  if (document.querySelector(`script[src="${src}"]`)) {
    callback();
    return;
  }

  const script = document.createElement('script');
  script.src = src;
  script.type = 'text/javascript';
  script.async = loadAsync;
  script.onload = callback;
  script.onerror = () => {
    console.error(`Failed to load script: ${src}`);
  };
  document.head.appendChild(script);
}

const ThirdPartyEmbed: FC<ThirdPartyEmbedProps> = ({
  snippet = '',
  async: loadAsync = true,
  notForDnt = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const parsed = useMemo(() => {return parseEmbedSnippet(snippet)}, [snippet]);
  const errorMessage = getEmbedErrorMessage(snippet, parsed.error);
  const dntBlocked = !notForDnt && isDntEnabled();

  useEffect(() => {
    if (dntBlocked || errorMessage) {
      if (errorMessage && snippet.trim()) {
        console.error(errorMessage);
      }
      return;
    }
    if (!containerRef.current) {
      return;
    }

    const container = containerRef.current;

    if (parsed.containerHtml) {
      container.innerHTML = parsed.containerHtml;
    }

    const onScriptLoad = createOnLoadCallback(parsed, containerRef);

    parsed.scriptSrcs.forEach((src) => {
      injectScriptToHead(src, loadAsync, onScriptLoad);
    });

    return () => {
      container.innerHTML = '';
    };
  }, [dntBlocked, snippet, loadAsync, parsed, errorMessage]);

  if (errorMessage) {
    return (
      <div
        className="third-party-embed"
        dangerouslySetInnerHTML={{ __html: `<!-- ${errorMessage} -->` }}
      />
    );
  }

  return <div className="third-party-embed" ref={containerRef} />;
};

export default ThirdPartyEmbed;
